/**
 * Declared UI-chrome labels for the create agent's single-button commit docks.
 *
 * These are the ONLY user-facing strings on the v3 planner path that are still
 * constants rather than model-authored copy. They head the one-button
 * Generate/Modify/Quote docks, where a heading is structural chrome rather than
 * conversational copy. The planner may override each per turn via the tool's
 * `groupTitle` arg; when it does not, the handler falls back to the matching
 * constant here.
 *
 * Pending full model authorship of commit-dock headings — track alongside the
 * zero-canned-copy roadmap.
 */
export const STAGE_GENERATE_FRONT_TITLE = "Preview this direction";
export const STAGE_GENERATE_BACK_TITLE = "Preview the back";
export const STAGE_MODIFY_TITLE = "Regenerate preview";
export const STAGE_QUOTE_TITLE = "Quote request";
export const STAGE_QUOTE_BEFORE_FRONT_TITLE = "Preview before quote";

/**
 * What a commit dock's button will actually do, and what it costs.
 *
 * The dock deliberately states the outcome instead of recapping the design.
 * The DesignSpec is internal state: rendered field-by-field it read as a form
 * to fill in rather than a decision to confirm, and its provenance keys leaked
 * raw dotted paths into customer-facing copy. What the customer is buying is
 * described in the conversation above the dock, authored by the model.
 */
export const COMMIT_EXPLAINER_GENERATE_FRONT =
  "Create a front preview of your dress for 1 credit. You can keep chatting to make changes first.";
export const COMMIT_EXPLAINER_GENERATE_BACK =
  "Create a back preview of your dress for 1 credit. You can keep chatting to make changes first.";
export const COMMIT_EXPLAINER_MODIFY =
  "Create an updated preview of your dress for 1 credit. You can keep chatting to make changes first.";

/**
 * The canonical label of each commit BUTTON the docks above head.
 *
 * The planner prompt tells the model to name the control exactly (see
 * create-agent-loop/nativeStep.ts) and both label resolvers — composeReply's
 * `requestedActionLabel` and widgets/parts' `stagedRequestedActionLabel` — map
 * the action id to these strings. Two builders shipped shorter variants of
 * their own, so a single back-design turn could render "Generate back" beside
 * "Generate back preview"; production has four customers who clicked both
 * believing they were different actions. One label per action id, everywhere.
 */
export const GENERATE_FRONT_LABEL = "Generate front";
export const GENERATE_BACK_LABEL = "Generate back";
export const MODIFY_FRONT_LABEL = "Regenerate front";
export const MODIFY_BACK_LABEL = "Regenerate back";
export const QUOTE_LABEL = "Get free quote";

/**
 * The workspace control names customers actually see for each commit label.
 *
 * The stable designer workspace (desktop footer and mobile action dock) owns
 * render and quote commits and names them "Generate preview", "Apply changes"
 * and "Get quote"; the shared reply style tells both planner and compose to use
 * those names and never the internal labels above. Copy validation must accept
 * either spelling — requiring only the internal label rejected every correctly
 * written staged reply and shipped the deterministic fallback on ~19% of turns
 * from 2026-09-10 (half of all first-session entry replies).
 */
export const WORKSPACE_ACTION_LABEL_ALIASES: Readonly<
  Record<string, readonly string[]>
> = {
  [GENERATE_FRONT_LABEL]: ["Generate preview"],
  [GENERATE_BACK_LABEL]: ["Generate back preview"],
  [MODIFY_FRONT_LABEL]: ["Apply changes"],
  [MODIFY_BACK_LABEL]: ["Apply changes"],
  [QUOTE_LABEL]: ["Get quote"],
};

/** The name the reply style tells the model to use for a staged commit label. */
export function workspaceActionLabel(label: string): string {
  return WORKSPACE_ACTION_LABEL_ALIASES[label]?.[0] ?? label;
}

/** A staged commit label plus the workspace names that invoke the same action. */
export function acceptedActionLabelNames(label: string): readonly string[] {
  return [label, ...(WORKSPACE_ACTION_LABEL_ALIASES[label] ?? [])];
}

/**
 * Every standing commit/navigation control a click can send as its turn text,
 * including the labels the unification above retired.
 *
 * A preset click posts the button's own label as the message (usePresetDispatch
 * sends `preset.label` with `hideUserMessage`), and restored transcripts still
 * carry buttons rendered under the old labels — so anything that MATCHES a
 * message against a known control has to keep accepting the retired strings.
 * Nothing about a click's authorization depends on this: the action surface
 * matches payload shape, never the label.
 *
 * Deliberately excludes the explore controls ("None of these fit", "Explore
 * this direction"): those clicks mean the options missed, which is the customer
 * expressing dissatisfaction rather than requesting an action.
 */
const ACTION_LABELS: readonly string[] = [
  GENERATE_FRONT_LABEL,
  GENERATE_BACK_LABEL,
  MODIFY_FRONT_LABEL,
  MODIFY_BACK_LABEL,
  QUOTE_LABEL,
  // Restored transcripts may still post controls rendered under these labels.
  "Generate preview",
  "Generate back preview",
  "Apply changes",
  "Generate",
  "Generate front",
  "Generate back",
  "Regenerate front",
  "Regenerate back",
  "Apply changes to back image",
  "Design the back",
  "Continue to back design",
  "Start over",
  "Restart",
  "Try again",
  "Buy credits",
  "Buy credits to design more",
];

function normalizeLabel(text: string): string {
  return text
    .trim()
    .replace(/[.!]+$/, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const NORMALIZED_ACTION_LABELS: ReadonlySet<string> = new Set(
  ACTION_LABELS.map(normalizeLabel)
);

/**
 * Whether a turn's text is exactly one of the standing controls — i.e. the turn
 * is a button click rather than something the customer composed.
 */
export function isKnownActionLabel(text: string | null | undefined): boolean {
  return (
    typeof text === "string" &&
    NORMALIZED_ACTION_LABELS.has(normalizeLabel(text))
  );
}
